/**
 * Pure, client-safe area row model (spec section 5.3): row types, per-sport stats, ranking and
 * "better nearby" selection. `rows.ts` re-exports everything here and adds the server readers.
 * L3's board rows import `rowStatsFor` from here so the stat choice is one rule everywhere.
 */
import { ELSEWHERE_SCORE_GAP } from "@/lib/companion/decision";
import type { LeaderboardEntry } from "@/lib/conditions/board";
import type { ConditionSafetyState } from "@/lib/conditions/safety-notes";
import type { AreaBoardSport, ForecastArea, SurfDifficulty } from "./types";

export type AreaRowStatus = "ranked" | "withheld" | "closed" | "stale" | "unscored";
export type WindRelation = "offshore" | "cross-shore" | "onshore";

export type AreaRowStats =
  | Readonly<{ kind: "surf"; swellM: number | null; periodS: number | null; windKmh: number | null; waterC: number | null; windRelation: WindRelation | null }>
  | Readonly<{ kind: "snow"; newSnow48hCm: number | null; depthCm: number | null; tempMinC: number | null; extent: number | null }>
  | Readonly<{ kind: "wind"; windKmh: number | null; waterC: number | null }>
  | Readonly<{ kind: "water"; waterC: number | null; swellM: number | null }>
  | Readonly<{ kind: "trail"; tempMinC: number | null; windKmh: number | null }>
  | Readonly<{ kind: "sailing"; windKmh: number | null; venueType: string | null }>;

export type AreaRowStatsKind = AreaRowStats["kind"];

export type AreaRow = Readonly<{
  slug: string;
  name: string;
  displayName: string;
  section: string | null;
  sectionName: string | null;
  locality: string | null;
  /** Null when withheld, closed or unscored. */
  score10: number | null;
  /** Published label, for example "Closed" for a v3 withheld piste day. */
  label: string | null;
  /** 1-based within the area, ranked rows only. */
  rank: number | null;
  status: AreaRowStatus;
  difficulty: SurfDifficulty | null;
  /** Surf: advanced or expert, or null difficulty on a reef or point. */
  experiencedOnly: boolean;
  beginnerFriendly: boolean;
  safetyState: ConditionSafetyState | null;
  stats: AreaRowStats;
  href: string;
  windowHref: string;
}>;

export type AreaSectionSummary = Readonly<{ key: string; name: string; best: AreaRow | null; rankedCount: number }>;

export type AreaBoard = Readonly<{
  area: ForecastArea;
  sport: AreaBoardSport;
  snapshotUpdatedISO: string | null;
  /** The whole snapshot is stale: no "best now" claims. */
  snapshotStale: boolean;
  /** Ranked rows first by score, then withheld, closed, stale, unscored by name. */
  rows: readonly AreaRow[];
  sections: readonly AreaSectionSummary[];
  memberCount: number;
}>;

/** Which stat family a board sport shows. No swell on MTB, wind or diving rows. */
export function rowStatsKindFor(sport: string): AreaRowStatsKind {
  switch (sport) {
    case "surf": case "bodyboarding": return "surf";
    case "piste": case "off-piste": case "touring": case "ski": return "snow";
    case "wind": case "foil": case "downwind-foil": case "wing-foil": return "wind";
    case "diving": case "swimming": case "swim": case "sup": return "water";
    case "mtb": return "trail";
    case "sailing": case "sailing-club": case "sailing-hp": return "sailing";
    default: return "water";
  }
}

type StatsEntry = Partial<Pick<LeaderboardEntry,
  "swellWaveHeightM" | "swellWavePeriodS" | "rawWindSpeedKmh" | "waterTempC" | "snowfallCm48h"
  | "snowDepthCm" | "tempMinC" | "extent" | "venueType">>;

function finite(value: number | null | undefined): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

/** Pure per-sport stat selection for a board entry (also used by L3's board rows). */
export function rowStatsFor(
  sport: string,
  entry: StatsEntry | null | undefined,
  windRelation: WindRelation | null = null,
): AreaRowStats {
  const e = entry ?? {};
  switch (rowStatsKindFor(sport)) {
    case "surf":
      return { kind: "surf", swellM: finite(e.swellWaveHeightM), periodS: finite(e.swellWavePeriodS), windKmh: finite(e.rawWindSpeedKmh), waterC: finite(e.waterTempC), windRelation };
    case "snow": {
      const openPct = finite(e.extent?.openPct);
      return { kind: "snow", newSnow48hCm: finite(e.snowfallCm48h), depthCm: finite(e.snowDepthCm), tempMinC: finite(e.tempMinC), extent: openPct === null ? null : openPct / 100 };
    }
    case "wind":
      return { kind: "wind", windKmh: finite(e.rawWindSpeedKmh), waterC: finite(e.waterTempC) };
    case "trail":
      return { kind: "trail", tempMinC: finite(e.tempMinC), windKmh: finite(e.rawWindSpeedKmh) };
    case "sailing":
      return { kind: "sailing", windKmh: finite(e.rawWindSpeedKmh), venueType: e.venueType ?? null };
    default:
      return { kind: "water", waterC: finite(e.waterTempC), swellM: finite(e.swellWaveHeightM) };
  }
}

function angleDiff(a: number, b: number): number {
  const d = Math.abs((((a - b) % 360) + 360) % 360);
  return d > 180 ? 360 - d : d;
}

/**
 * Wind against a beach: offshore when the wind blows from within 60 degrees of the direction
 * opposite the beach's facing, onshore within 60 degrees of the facing, else cross-shore.
 */
export function windRelationFor(windFromDeg: number | null | undefined, facingDeg: number | null | undefined): WindRelation | null {
  const wind = finite(windFromDeg);
  const facing = finite(facingDeg);
  if (wind === null || facing === null) return null;
  if (angleDiff(wind, facing + 180) <= 60) return "offshore";
  if (angleDiff(wind, facing) <= 60) return "onshore";
  return "cross-shore";
}

/** Surf ability rule: advanced or expert, or an untagged reef or point. */
export function isExperiencedOnly(sport: string, difficulty: SurfDifficulty | null, spotType: string | null): boolean {
  if (sport !== "surf" && sport !== "bodyboarding") return false;
  if (difficulty === "advanced" || difficulty === "expert") return true;
  return difficulty === null && (spotType === "reef" || spotType === "point");
}

const STATUS_ORDER: Readonly<Record<AreaRowStatus, number>> = { ranked: 0, withheld: 1, closed: 2, stale: 3, unscored: 4 };

/**
 * Pure: ranked rows by score (a red-flagged row is never first while an unflagged ranked row
 * exists), then withheld, closed, stale and unscored rows by name. Ranks are recomputed 1-based
 * over the ranked rows. When every ranked row is red-flagged (including a single ranked row)
 * no row carries a rank, so a red flag never sits beside "#1" (spec section 5.3).
 */
/** The fields `rankAreaRows` reads: the area page's browser clock re-ranks these alone. */
export type RankableAreaRow = Pick<AreaRow, "status" | "score10" | "displayName" | "safetyState" | "rank">;

export function rankAreaRows<T extends RankableAreaRow>(rows: readonly T[]): readonly T[] {
  const ranked = rows
    .filter((row) => row.status === "ranked" && row.score10 !== null)
    .sort((a, b) => (b.score10 ?? 0) - (a.score10 ?? 0) || a.displayName.localeCompare(b.displayName, "en-GB"));
  if (ranked.length > 1 && ranked[0].safetyState === "flagged-red") {
    const firstClear = ranked.findIndex((row) => row.safetyState !== "flagged-red");
    if (firstClear > 0) ranked.unshift(...ranked.splice(firstClear, 1));
  }
  const rest = rows
    .filter((row) => !(row.status === "ranked" && row.score10 !== null))
    .map((row): T => row.status === "ranked" ? { ...row, status: "unscored" as const } : row)
    .sort((a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status] || a.displayName.localeCompare(b.displayName, "en-GB"));
  const anyClear = ranked.some((row) => row.safetyState !== "flagged-red");
  return [
    ...ranked.map((row, index): T => ({ ...row, rank: anyClear ? index + 1 : null })),
    ...rest.map((row): T => ({ ...row, rank: null })),
  ];
}

/** Section summaries over already ranked rows: best (first unflagged ranked row) and ranked count. */
export function summariseSections(
  sections: readonly Readonly<{ key: string; name: string }>[],
  rows: readonly AreaRow[],
): readonly AreaSectionSummary[] {
  return sections.map((section) => {
    const ranked = rows.filter((row) => row.section === section.key && row.status === "ranked");
    return {
      key: section.key,
      name: section.name,
      best: ranked.find((row) => row.safetyState !== "flagged-red") ?? null,
      rankedCount: ranked.length,
    };
  });
}

/**
 * Pure: up to `limit` same-area ranked rows that beat the spot by at least `minGap10`, best
 * first, never red-flagged. A spot without a score is beaten by any unflagged ranked row.
 * Rows keep `experiencedOnly`: anything rendering them must show the "Experienced only" label
 * beside such a row (spec section 5.5), and the spot verdict skips them for a spot that is not
 * itself experienced only (see spot-verdict.ts).
 */
export function betterNearbyRows(
  board: Pick<AreaBoard, "rows" | "snapshotStale">,
  spotSlug: string,
  options: Readonly<{ limit?: number; minGap10?: number }> = {},
): readonly AreaRow[] {
  if (board.snapshotStale) return [];
  const limit = options.limit ?? 3;
  const gap = options.minGap10 ?? ELSEWHERE_SCORE_GAP;
  const spot = board.rows.find((row) => row.slug === spotSlug);
  const base = spot?.status === "ranked" ? spot.score10 : null;
  return board.rows
    .filter((row) => row.slug !== spotSlug && row.status === "ranked" && row.score10 !== null
      && row.safetyState !== "flagged-red"
      && (base === null || row.score10 >= base + gap - 1e-9))
    .slice(0, Math.max(0, limit));
}
